import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import {
  ArrowLeft,
  Building2,
  FileText,
  Filter,
  History,
  List as ListIcon,
  ListPlus,
  ListTodo,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquare,
  Network,
  Paperclip,
  Pencil,
  Phone,
  Plus,
  StickyNote,
  Tag,
  Trash2,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { recordWhatsappClick } from '../../lib/distribution';
import { whatsappLink } from '../../lib/format';
import {
  apiAssetUrl,
  getLead,
  leadActions,
  listAttendants,
  lists as listsApi,
  setLeadLists,
  setLeadTags,
  tags as tagsApi,
  updateLead,
  uploadLeadImage,
} from '../../lib/resources';
import { useLeadRefresh } from '../../lib/use-lead-refresh';
import { LeadProposalsSection } from '../../pages/sales/LeadProposals';
import type { Lead } from '../../types';
import {
  Avatar,
  ConfirmDialog,
  ErrorNote,
  KindBadge,
  Loading,
  TagPill,
  inputClass,
} from '../ui';
import {
  ActivitiesSection,
  AddressesSection,
  AttendancesSection,
  BusinessesSection,
  CompaniesSection,
  CustomFieldsSection,
  FilesSection,
  HierarchySection,
  HistorySection,
  NotesSection,
  PeopleSection,
  ProfileSection,
} from './LeadSections';

export type SectionKey =
  | 'perfil'
  | 'campos'
  | 'negocios'
  | 'atividades'
  | 'atendimentos'
  | 'historicos'
  | 'hierarquia'
  | 'pessoas'
  | 'empresas'
  | 'enderecos'
  | 'arquivos'
  | 'notas'
  | 'propostas';

const SECTIONS: Array<[SectionKey, string, LucideIcon]> = [
  ['perfil', 'Perfil', UserRound],
  ['campos', 'Campos adicionais', ListPlus],
  ['negocios', 'Negócios', Filter],
  // Só para quem tem papel de vendas (o menu filtra); cada um vê só as próprias propostas.
  ['propostas', 'Propostas de venda', FileText],
  ['atividades', 'Atividades', ListTodo],
  ['atendimentos', 'Atendimentos', MessageSquare],
  ['historicos', 'Históricos', History],
  ['hierarquia', 'Hierarquia', Network],
  ['pessoas', 'Pessoas', Users],
  ['empresas', 'Empresas', Building2],
  ['enderecos', 'Endereços', MapPin],
  ['arquivos', 'Arquivos', Paperclip],
  ['notas', 'Notas', StickyNote],
];

/**
 * Ficha do lead (doc 02, 6): drawer com a coluna esquerda fixa (avatar, tipo,
 * tags, listas, atendente, telefone, menu das 12 seções) e a seção ativa à direita.
 * Também é a tela /leads/:id (modo página).
 */
export function LeadDrawer({
  leadId,
  onClose,
  asPage = false,
}: {
  leadId: string;
  onClose: () => void;
  asPage?: boolean;
}) {
  const [section, setSection] = useState<SectionKey>('perfil');
  const query = useQuery({ queryKey: ['lead', leadId], queryFn: () => getLead(leadId) });

  const body = (
    <div className={`flex ${asPage ? 'min-h-full' : 'h-full'} w-full bg-white`}>
      {query.isLoading ? (
        <Loading label="Carregando ficha…" />
      ) : !query.data ? (
        <div className="p-6">
          <ErrorNote>{errorMessage(query.error, 'Lead não encontrado')}</ErrorNote>
        </div>
      ) : (
        <>
          <LeadSummaryHeader
            lead={query.data}
            section={section}
            onSection={setSection}
            onClose={onClose}
            asPage={asPage}
          />
          <section className="min-w-0 flex-1 overflow-y-auto p-6">
            <h2 className="mb-4 text-base font-semibold text-slate-900">
              {SECTIONS.find(([key]) => key === section)?.[1]}
            </h2>
            <SectionBody lead={query.data} section={section} />
          </section>
        </>
      )}
    </div>
  );

  if (asPage) return body;
  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-900/30"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="h-full w-full max-w-5xl shadow-2xl">{body}</div>
    </div>
  );
}

/** Uma seção da ficha, com o título — usada pelo detalhe do negócio (DC1c). */
export function LeadSectionPanel({ lead, section }: { lead: Lead; section: SectionKey }) {
  return (
    <>
      <h2 className="mb-4 text-base font-semibold text-slate-900">
        {SECTIONS.find(([key]) => key === section)?.[1]}
      </h2>
      <SectionBody lead={lead} section={section} />
    </>
  );
}

function SectionBody({ lead, section }: { lead: Lead; section: SectionKey }) {
  switch (section) {
    case 'perfil':
      return <ProfileSection lead={lead} />;
    case 'campos':
      return <CustomFieldsSection lead={lead} />;
    case 'negocios':
      return <BusinessesSection lead={lead} />;
    case 'atividades':
      return <ActivitiesSection lead={lead} />;
    case 'atendimentos':
      return <AttendancesSection lead={lead} />;
    case 'historicos':
      return <HistorySection lead={lead} />;
    case 'hierarquia':
      return <HierarchySection lead={lead} />;
    case 'pessoas':
      return <PeopleSection lead={lead} />;
    case 'empresas':
      return <CompaniesSection lead={lead} />;
    case 'enderecos':
      return <AddressesSection lead={lead} />;
    case 'arquivos':
      return <FilesSection lead={lead} />;
    case 'notas':
      return <NotesSection lead={lead} />;
    case 'propostas':
      return <LeadProposalsSection leadId={lead.id} />;
  }
}

/** Coluna esquerda da ficha — reaproveitada no detalhe do negócio (DC1c). */
export function LeadSummaryHeader({
  lead,
  section,
  onSection,
  onClose,
  asPage,
}: {
  lead: Lead;
  section: SectionKey;
  onSection: (section: SectionKey) => void;
  onClose: () => void;
  asPage: boolean;
}) {
  const { can } = useAuth();
  const canWrite = can('leads_admin', 'leads_member_operator');
  const refresh = useLeadRefresh(lead.id);
  const fileInput = useRef<HTMLInputElement>(null);
  const [picker, setPicker] = useState<'tags' | 'lists' | null>(null);
  const [deleting, setDeleting] = useState(false);

  const tags = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
    enabled: picker === 'tags',
  });
  const lists = useQuery({
    queryKey: ['lists'],
    queryFn: () => listsApi.list(),
    enabled: picker === 'lists',
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });

  const toggleTag = useMutation({
    mutationFn: (id: string) => {
      const ids = lead.tags.map((tag) => tag.id);
      return setLeadTags(
        lead.id,
        ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id],
      );
    },
    onSuccess: refresh,
  });
  const toggleList = useMutation({
    mutationFn: (id: string) => {
      const ids = lead.lists.map((list) => list.id);
      return setLeadLists(
        lead.id,
        ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id],
      );
    },
    onSuccess: refresh,
  });
  const attendant = useMutation({
    mutationFn: (userId: string) => updateLead(lead.id, { attendant: userId || null }),
    onSuccess: refresh,
  });
  const image = useMutation({
    mutationFn: (file: File) => uploadLeadImage(lead.id, file),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => leadActions.remove({ ids: [lead.id] }),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  const avatar = lead.image?.startsWith('/api/') ? apiAssetUrl(lead.image) : lead.image;
  const wa = whatsappLink(lead.rawPhone);
  const error = toggleTag.error ?? toggleList.error ?? attendant.error ?? image.error;

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-slate-200 bg-slate-50">
      <div
        className="h-14"
        style={{
          background:
            'linear-gradient(90deg, var(--crm-primary), color-mix(in oklab, var(--crm-primary) 45%, var(--crm-accent)))',
        }}
      />
      <div className="-mt-7 px-4">
        <div className="relative inline-block rounded-full ring-4 ring-slate-50">
          <Avatar name={lead.name} src={avatar} size="lg" />
          {canWrite && (
            <>
              <button
                type="button"
                aria-label="Trocar foto"
                onClick={() => fileInput.current?.click()}
                className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-1 text-slate-600 shadow"
              >
                <Pencil size={11} />
              </button>
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) image.mutate(file);
                  event.target.value = '';
                }}
              />
            </>
          )}
        </div>
        <div className="mt-2 flex items-start justify-between gap-2">
          <p className="text-[15px] font-semibold text-slate-900">{lead.name}</p>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            aria-label={asPage ? 'Voltar' : 'Fechar'}
          >
            {asPage ? <ArrowLeft size={16} /> : <X size={16} />}
          </button>
        </div>
        <div className="mt-1 flex gap-1">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${lead.stage === 'CLIENT' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
          >
            {lead.stage === 'CLIENT' ? 'Cliente' : 'Lead'}
          </span>
          <KindBadge kind={lead.kind} />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1">
          {lead.tags.map((tag) => (
            <TagPill
              key={tag.id}
              tag={tag}
              onRemove={canWrite ? () => toggleTag.mutate(tag.id) : undefined}
            />
          ))}
          {canWrite && (
            <button
              type="button"
              className="rounded-full border border-dashed border-slate-300 px-2 text-xs text-slate-500"
              onClick={() => setPicker(picker === 'tags' ? null : 'tags')}
              title="Adicionar tag"
            >
              <span className="flex items-center gap-0.5">
                <Plus size={11} /> <Tag size={11} />
              </span>
            </button>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {lead.lists.map((list) => (
            <span
              key={list.id}
              className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-600 ring-1 ring-slate-200"
            >
              <ListIcon size={11} className="mr-1 inline" />
              {list.name}
            </span>
          ))}
          {canWrite && (
            <button
              type="button"
              className="rounded-full border border-dashed border-slate-300 px-2 text-xs text-slate-500"
              onClick={() => setPicker(picker === 'lists' ? null : 'lists')}
              title="Adicionar à lista"
            >
              <span className="flex items-center gap-0.5">
                <Plus size={11} /> <ListIcon size={11} />
              </span>
            </button>
          )}
        </div>

        {picker && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
            {(picker === 'tags' ? tags.data?.data : lists.data?.data)?.map((item) => {
              const active = (picker === 'tags' ? lead.tags : lead.lists).some(
                (current) => current.id === item.id,
              );
              return (
                <label key={item.id} className="flex items-center gap-2 py-0.5 text-sm">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() =>
                      picker === 'tags' ? toggleTag.mutate(item.id) : toggleList.mutate(item.id)
                    }
                  />
                  {item.name}
                </label>
              );
            })}
          </div>
        )}

        <label className="mt-3 block text-[11px] font-medium uppercase tracking-wide text-slate-400">
          Atendente responsável
          <select
            disabled={!canWrite}
            className={`${inputClass} mt-1 bg-white normal-case tracking-normal`}
            value={lead.attendantId ?? ''}
            onChange={(event) => attendant.mutate(event.target.value)}
          >
            <option value="">+ Atribuir atendente</option>
            {(attendants.data?.data ?? []).map((item) => (
              <option key={item.userId} value={item.userId}>
                {item.name}
              </option>
            ))}
          </select>
        </label>

        <p className="mt-3 flex items-center gap-2 text-[13px] text-slate-700">
          <Phone size={14} className="shrink-0 text-slate-400" />
          <span className="truncate">{lead.phone ?? 'Sem telefone'}</span>
          {wa && (
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              onClick={() => recordWhatsappClick(lead.id)}
              className="ml-auto text-emerald-600 hover:text-emerald-700"
              title="Abrir conversa no WhatsApp"
              aria-label="Abrir conversa no WhatsApp"
            >
              <MessageCircle size={15} />
            </a>
          )}
        </p>
        {lead.email && (
          <p className="mt-1.5 flex items-center gap-2 text-[13px] text-slate-700">
            <Mail size={14} className="shrink-0 text-slate-400" />
            <span className="truncate">{lead.email}</span>
          </p>
        )}
        {error && <p className="mt-2 text-xs text-red-600">{errorMessage(error)}</p>}
      </div>

      <nav className="mt-4 flex-1 overflow-y-auto px-2 pb-4" aria-label="Seções da ficha">
        {SECTIONS.filter(
          ([key]) => key !== 'propostas' || can('sales_admin', 'sales_member'),
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => onSection(key)}
            className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-left text-[13px] ${
              section === key
                ? 'bg-white font-semibold text-slate-900 shadow-sm'
                : 'text-slate-600 hover:bg-white'
            }`}
          >
            <Icon size={15} className="shrink-0" />
            {label}
          </button>
        ))}
        {can('leads_admin') && (
          <button
            type="button"
            onClick={() => setDeleting(true)}
            className="mt-3 flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-left text-[13px] text-red-600 hover:bg-red-50"
          >
            <Trash2 size={15} /> Excluir lead
          </button>
        )}
      </nav>

      {deleting && (
        <ConfirmDialog
          title="Excluir lead?"
          message={
            <p>
              <strong>{lead.name}</strong> vai para a Lixeira com os negócios e as atividades.
              Dá para restaurar por 30 dias.
            </p>
          }
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate()}
          onClose={() => setDeleting(false)}
        />
      )}
    </aside>
  );
}
